import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./LanternPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Lantern" (/lantern) — an ONBOARDING (first-run) paywall, built to the
// standing directive: SECURITY & PRIVACY.
//
// The mechanic is one no other paywall here uses: your memories live in a DARK,
// PRIVATE ROOM, and ONLY THE LIGHT YOU CARRY reveals them — a warm lantern glow
// that follows your finger. Where your light falls, your photos resolve into
// full colour; lift it away and they slip back into the private dark. To anyone
// else the room stays dark. Sweep the light and, one by one, you DISCOVER the
// four real protections waiting in the room — they wake as your light passes
// over them and stay lit (a quiet completion arc), never a checklist that fills.
//
// Deliberately avoids every taken mechanic (lock/seal/zip/deadbolt/dial/slider/
// scrub/switch/handshake/glass). The interaction IS the metaphor: privacy is
// darkness, and only your light — only YOU — brings your memories into view.
// Register: intimate and warm ("in here it's just you and your memories"), not
// cold-security, not fear, not countdowns.
//
// Honest & App-Store compliant: the featured, most-prominent price is the YEARLY
// plan (7-day free trial, per-month broken out, honest Save %); monthly is a
// small secondary line. No scarcity/countdown, no data-loss claim — the dark is
// privacy, not deletion; Premium only ADDS protection, and nothing is ever
// hidden from you or removed. Reuses only the house font, colour tokens and the
// bundled byheart memory photos. Renders standalone on debug data; no native
// injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// A curated wall of memories, scattered in the private room. Positions are % of
// the room; the light reveals whichever ones it falls across.
type Tile = { src: string; x: number; y: number; w: number; rot: number }
const TILES: Tile[] = [
  { src: "m03", x: 20, y: 20, w: 118, rot: -7 },
  { src: "m07", x: 52, y: 15, w: 96, rot: 5 },
  { src: "m12", x: 81, y: 22, w: 108, rot: 8 },
  { src: "m01", x: 16, y: 40, w: 104, rot: 6 },
  { src: "m09", x: 45, y: 37, w: 132, rot: -4 },
  { src: "m16", x: 82, y: 43, w: 100, rot: -8 },
  { src: "m05", x: 24, y: 60, w: 112, rot: 9 },
  { src: "m14", x: 57, y: 58, w: 120, rot: -6 },
  { src: "m08", x: 85, y: 63, w: 92, rot: 7 },
  { src: "m11", x: 38, y: 77, w: 98, rot: 4 },
  { src: "m06", x: 70, y: 78, w: 108, rot: -9 },
].map((t) => ({ ...t, src: photo(t.src) }))

// The four REAL Premium protections, waiting in the room. Placed in the gaps
// between memories; each wakes when your light finds it, and stays lit.
type Guard = {
  id: string
  label: string
  sub: string
  x: number
  y: number
  icon: JSX.Element
}
const GUARDS: Guard[] = [
  {
    id: "unlock",
    label: "Only-you unlock",
    sub: "Face ID / PIN",
    x: 72,
    y: 39,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M7.5 10V7.6a4.5 4.5 0 0 1 9 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="5"
          y="10"
          width="14"
          height="9.6"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.4" r="1.4" fill="var(--ln-hole)" />
      </svg>
    ),
  },
  {
    id: "backup",
    label: "Encrypted backup",
    sub: "End-to-end",
    x: 28,
    y: 39,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M12 3.4l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.7 12.2l2.2 2.2 4.4-4.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "alerts",
    label: "Break-in alerts",
    sub: "Catches intruders",
    x: 74,
    y: 60,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M2.5 12S6 5.6 12 5.6 21.5 12 21.5 12 18 18.4 12 18.4 2.5 12 2.5 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="2.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    ),
  },
  {
    id: "sync",
    label: "Private sync",
    sub: "Your devices only",
    x: 26,
    y: 60,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M5 12a7 7 0 0 1 11.9-5M19 12A7 7 0 0 1 7.1 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M16.4 3.6V7h-3.4M7.6 20.4V17h3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
]

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)

function LanternPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // Which protections the light has discovered (persists once found).
  const [found, setFound] = useState<Record<string, boolean>>({})
  const foundRef = useRef<Record<string, boolean>>({})
  const allFound = GUARDS.every((g) => found[g.id])

  const rootRef = useRef<HTMLDivElement | null>(null)
  const litRef = useRef<HTMLDivElement | null>(null)
  const glowRef = useRef<HTMLDivElement | null>(null)
  const plaqueRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Light geometry, all in ref-space so the rAF loop never re-renders React.
  const cur = useRef({ x: 0, y: 0 }) // current (eased) light centre, px
  const tgt = useRef({ x: 0, y: 0 }) // target light centre, px
  const started = useRef(false) // has the user grabbed the light yet
  const rafId = useRef<number | null>(null)
  const demoStart = useRef(0)

  useEffect(() => {
    actions.setHeaderInsetColor("#0a0b12")
    actions.setFooterInsetColor(darkMode ? "#0a0b12" : "#0c0d16")
  }, [actions, darkMode])

  // A gentle guided path for the opening auto-demo: sweeps the light across the
  // memories and over each protection so the payoff reads in ~3s, then hands
  // control to the user. Points are fractions of the room (0..1).
  const demoPath = useMemo(
    () => [
      { x: 0.28, y: 0.39 },
      { x: 0.72, y: 0.39 },
      { x: 0.74, y: 0.6 },
      { x: 0.26, y: 0.6 },
      { x: 0.5, y: 0.48 },
    ],
    [],
  )

  const markNear = useCallback((px: number, py: number, radius: number) => {
    let changed = false
    for (const g of GUARDS) {
      if (foundRef.current[g.id]) continue
      const el = plaqueRefs.current[g.id]
      const root = rootRef.current
      if (!el || !root) continue
      const r = root.getBoundingClientRect()
      const e = el.getBoundingClientRect()
      const cx = e.left - r.left + e.width / 2
      const cy = e.top - r.top + e.height / 2
      const d = Math.hypot(cx - px, cy - py)
      if (d < radius * 0.85) {
        foundRef.current[g.id] = true
        changed = true
      }
    }
    if (changed) setFound({ ...foundRef.current })
  }, [])

  // Radius of the revealing light, in px, sized to the room.
  const radius = useCallback(() => {
    const root = rootRef.current
    const w = root?.clientWidth ?? 380
    return Math.max(120, Math.min(190, w * 0.42))
  }, [])

  const paint = useCallback(() => {
    const root = rootRef.current
    if (!root) return
    const r = radius()
    root.style.setProperty("--mx", `${cur.current.x}px`)
    root.style.setProperty("--my", `${cur.current.y}px`)
    root.style.setProperty("--r", `${r}px`)
    if (glowRef.current) {
      glowRef.current.style.left = `${cur.current.x}px`
      glowRef.current.style.top = `${cur.current.y}px`
    }
    markNear(cur.current.x, cur.current.y, r)
  }, [radius, markNear])

  // Reduced motion / no-JS-friendly static reveal: light rests centred with a
  // wide radius, every protection already discovered, nothing animates.
  useEffect(() => {
    if (!reduce) return
    const root = rootRef.current
    if (!root) return
    const w = root.clientWidth
    const h = root.clientHeight
    cur.current = { x: w * 0.5, y: h * 0.42 }
    tgt.current = { ...cur.current }
    root.style.setProperty("--r", `${Math.max(w, h) * 0.62}px`)
    root.style.setProperty("--mx", `${cur.current.x}px`)
    root.style.setProperty("--my", `${cur.current.y}px`)
    if (glowRef.current) {
      glowRef.current.style.left = `${cur.current.x}px`
      glowRef.current.style.top = `${cur.current.y}px`
    }
    foundRef.current = { unlock: true, backup: true, alerts: true, sync: true }
    setFound({ ...foundRef.current })
  }, [reduce])

  // Main animation loop: eased follow of the target, plus the opening demo.
  useEffect(() => {
    if (reduce) return
    const root = rootRef.current
    if (!root) return

    const w = root.clientWidth
    const h = root.clientHeight
    cur.current = { x: w * 0.3, y: h * 0.3 }
    tgt.current = { x: w * 0.3, y: h * 0.3 }
    demoStart.current = performance.now()
    paint()

    const loop = (now: number) => {
      // Drive the target from the demo path until the user takes over.
      if (!started.current) {
        const seg = 720 // ms per demo leg
        const t = (now - demoStart.current) / seg
        const i = Math.floor(t)
        if (i >= demoPath.length - 1) {
          const last = demoPath[demoPath.length - 1]
          tgt.current = { x: last.x * w, y: last.y * h }
        } else {
          const a = demoPath[i]
          const b = demoPath[i + 1]
          const f = t - i
          const e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2 // ease-in-out
          tgt.current = {
            x: (a.x + (b.x - a.x) * e) * w,
            y: (a.y + (b.y - a.y) * e) * h,
          }
        }
      }
      // Ease current toward target.
      cur.current.x += (tgt.current.x - cur.current.x) * 0.16
      cur.current.y += (tgt.current.y - cur.current.y) * 0.16
      paint()
      rafId.current = requestAnimationFrame(loop)
    }
    rafId.current = requestAnimationFrame(loop)
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current)
    }
  }, [reduce, paint, demoPath])

  const moveTo = useCallback((clientX: number, clientY: number) => {
    const root = rootRef.current
    if (!root) return
    const r = root.getBoundingClientRect()
    tgt.current = { x: clientX - r.left, y: clientY - r.top }
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (reduce) return
      started.current = true
      moveTo(e.clientX, e.clientY)
    },
    [moveTo, reduce],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (reduce) return
      // Follow on hover (desktop) and while dragging (touch). e.buttons is 0 on
      // hover, non-zero while pressed — both should move the light.
      started.current = true
      moveTo(e.clientX, e.clientY)
    },
    [moveTo, reduce],
  )

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_lantern_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Protect my memories"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div
      className={`${styles.root} ${allFound ? styles.allFound : ""}`}
      ref={rootRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
    >
      {/* The dark private room: memories present but veiled */}
      <div className={styles.tilesDim} aria-hidden>
        {TILES.map((t) => (
          <span
            key={t.src}
            className={styles.tile}
            style={{
              left: `${t.x}%`,
              top: `${t.y}%`,
              width: t.w,
              transform: `translate(-50%, -50%) rotate(${t.rot}deg)`,
              backgroundImage: `url(${t.src})`,
            }}
          />
        ))}
      </div>

      {/* The same memories in full colour — shown only where your light falls */}
      <div className={styles.tilesLit} ref={litRef} aria-hidden>
        {TILES.map((t) => (
          <span
            key={t.src}
            className={styles.tile}
            style={{
              left: `${t.x}%`,
              top: `${t.y}%`,
              width: t.w,
              transform: `translate(-50%, -50%) rotate(${t.rot}deg)`,
              backgroundImage: `url(${t.src})`,
            }}
          />
        ))}
      </div>

      {/* Protections waiting in the room — they wake as the light finds them */}
      <div className={styles.plaques} aria-hidden>
        {GUARDS.map((g) => (
          <div
            key={g.id}
            ref={(el) => {
              plaqueRefs.current[g.id] = el
            }}
            className={`${styles.plaque} ${found[g.id] ? styles.plaqueOn : ""}`}
            style={{ left: `${g.x}%`, top: `${g.y}%` }}
          >
            <span className={styles.plaqueIcon}>{g.icon}</span>
            <span className={styles.plaqueText}>
              <b>{g.label}</b>
              <i>{g.sub}</i>
            </span>
          </div>
        ))}
      </div>

      {/* The warm lantern light itself */}
      <div className={styles.glow} ref={glowRef} aria-hidden />
      {/* Soft dark edges to keep the room feeling enclosed */}
      <div className={styles.vignette} aria-hidden />

      {/* Foreground content */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
              <path
                d="M8 10V7.6a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--ln-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker}>Welcome to your vault</span>
          <h1 className={styles.title}>
            In here, only your
            <br />
            light finds them.
          </h1>
          <p className={styles.sub}>
            Your photos and videos rest in the dark, private by default. Carry
            the light to bring them back — and discover what Premium keeps watch
            over.
          </p>
        </header>

        <div className={styles.hint} data-done={allFound}>
          {allFound ? (
            <span>
              <i className={styles.hintDot} /> Full protection found ·
              encrypted, backed up, synced &amp; watched
            </span>
          ) : (
            <span>
              <i className={styles.hintDot} /> Move the light —{" "}
              {GUARDS.filter((g) => found[g.id]).length}/4 protections found
            </span>
          )}
        </div>
      </div>

      {/* Bottom glass sill: pricing + CTA, yearly featured */}
      <div
        className={styles.panel}
        style={{ paddingBottom: bottomInset + 14 }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
      >
        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>
        <p className={styles.reassure}>
          Your memories are always yours — nothing is ever hidden from you or
          deleted. The dark is privacy; Premium only adds protection.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default LanternPaywall
